import { useEffect, useRef, useState } from 'react'

/**
 * Saves previous value for a given variable.
 * ATTENTION: only saves the previous value of the last SUCCESSFUL render, meaning if rendering was interrupted by e.g a rerender the previous value is LOST!
 *
 * @param value for which the previous entry is requested
 * @returns previous entry of value
 */
const usePrevious = <T,>(value: T): T | undefined => {
  const ref = useRef<T>()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

export default usePrevious

/**
 * usePrevious hook that persists the previous value across renders.
 * @param value for which the previous entry is requested
 * @param isEqualFunc optional function to compare the previous and current value
 * @returns previous persisted entry of value
 */
export const usePreviousPersistent = <T,>(value: T, isEqualFunc?: (prev: T, current: T) => boolean): T | null => {
  const [state, setState] = useState<{ value: T; prev: T | null }>({
    value: value,
    prev: null,
  })

  const current = state.value

  if (isEqualFunc ? !isEqualFunc(value, current) : value !== current) {
    setState({
      value: value,
      prev: current,
    })
  }

  return state.prev
}
